import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { artifactsApi } from "@/api/artifacts";
import type { Artifact, CreateArtifactInput } from "@/api/artifacts";
import { queryKeys } from "@/lib/query-keys";
import { FileBox, Plus, Trash2, ExternalLink, Upload } from "lucide-react";

const backendVariant: Record<string, "success" | "warning" | "default" | "secondary"> = {
  sharepoint: "success",
  teams: "success",
  local: "secondary",
};

export function Artifacts() {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const { data: artifacts = [], isLoading } = useQuery({
    queryKey: queryKeys.artifacts.byCompany(tenantId),
    queryFn: () => artifactsApi.list(tenantId),
  });

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<Partial<CreateArtifactInput>>({
    name: "",
    mimeType: "application/json",
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.artifacts.byCompany(tenantId) });

  const createMutation = useMutation({
    mutationFn: () =>
      artifactsApi.create({
        companyId: tenantId,
        name: form.name!,
        mimeType: form.mimeType!,
      }),
    onSuccess: () => {
      invalidate();
      setShowCreate(false);
      setForm({ name: "", mimeType: "application/json" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => artifactsApi.delete(id),
    onSuccess: invalidate,
  });

  return (
    <CenterPanel title="Artifacts">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          {artifacts.length} artifact{artifacts.length !== 1 ? "s" : ""}
        </p>
        <Button size="sm" onClick={() => setShowCreate(!showCreate)}>
          <Plus size={16} className="mr-1.5" />
          New Artifact
        </Button>
      </div>

      {showCreate && (
        <Card className="mb-4">
          <CardContent className="space-y-3 pt-4">
            <div className="flex gap-3">
              <Input
                placeholder="Artifact name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="flex-1"
              />
              <select
                value={form.mimeType}
                onChange={(e) => setForm({ ...form, mimeType: e.target.value })}
                className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
              >
                <option value="application/json">JSON</option>
                <option value="text/plain">Text</option>
                <option value="text/markdown">Markdown</option>
                <option value="application/pdf">PDF</option>
                <option value="text/csv">CSV</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={!form.name?.trim() || createMutation.isPending}
                onClick={() => createMutation.mutate()}
              >
                Create
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <p className="text-sm text-text-muted">Loading...</p>
      ) : artifacts.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <FileBox size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">No artifacts yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {artifacts.map((a: Artifact) => (
            <Card key={a.id}>
              <CardContent className="flex items-center justify-between py-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
                    <FileBox size={20} className="text-accent" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">{a.name}</p>
                    <p className="text-xs text-text-muted">
                      {a.mimeType}
                      {a.sizeBytes != null ? ` \u00b7 ${(a.sizeBytes / 1024).toFixed(1)} KB` : ""}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {a.storageBackend && (
                    <Badge variant={backendVariant[a.storageBackend] ?? "default"}>
                      {a.storageBackend}
                    </Badge>
                  )}
                  {a.webUrl && (
                    <a href={a.webUrl} target="_blank" rel="noopener noreferrer">
                      <Button size="sm" variant="ghost">
                        <ExternalLink size={14} />
                      </Button>
                    </a>
                  )}
                  {!a.storageBackend || a.storageBackend === "local" ? (
                    <Button size="sm" variant="ghost" title="Export to SharePoint (requires M365 connection)">
                      <Upload size={14} />
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => deleteMutation.mutate(a.id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </CenterPanel>
  );
}
